/**
 * Shared error handling utilities for mutation hooks.
 *
 * All mutation hooks should use `getErrorMessage()` to extract
 * user-friendly error messages from API errors.
 *
 * @see docs/error-messages.md
 */

import { ApiError } from '@/api/client'

/**
 * Extract user-friendly error message from any error.
 *
 * Priority:
 * 1. ApiError.getUserMessage() — backend's user-friendly message
 * 2. Error.message — fallback for non-API errors
 * 3. Generic message — last resort
 *
 * @example
 * ```ts
 * useMutation({
 *   onError: (error) => {
 *     toast.error(getErrorMessage(error))
 *   },
 * })
 * ```
 */
export function getErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.getUserMessage()
  }

  if (error instanceof Error) {
    return error.message
  }

  return 'An unexpected error occurred. Please try again.'
}

/**
 * Get the error code from an error, if available.
 */
export function getErrorCode(error: unknown): string | undefined {
  if (error instanceof ApiError) {
    return error.code
  }
  return undefined
}

/**
 * Get field-level validation errors, if available.
 */
export function getFieldErrors(error: unknown): Record<string, string> | undefined {
  if (error instanceof ApiError && error.hasFieldErrors()) {
    return error.fields
  }
  return undefined
}

/**
 * Get request ID for support/debugging, if available.
 */
export function getRequestId(error: unknown): string | undefined {
  if (error instanceof ApiError) {
    return error.requestId
  }
  return undefined
}
